'use client';

import React, { useEffect, useState } from 'react';

import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
// Import from /pure: the default entry auto-injects js.stripe.com on import,
// which fires "Failed to load Stripe.js" as an unhandled rejection for every
// tenant on /book — including Moyasar-only ones that never use Stripe. /pure
// exposes the same loadStripe but defers the script until we actually call it.
import { loadStripe } from '@stripe/stripe-js/pure';

import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@kit/ui/sheet';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';
import { useIsMobile } from '@kit/ui/use-mobile';
import { cn } from '@kit/ui/utils';

import { useBookingPresentation } from './booking-presentation';

interface StripeCheckoutFormProps {
  isOpen: boolean;
  onClose: () => void;
  clientSecret: string | null;
  publishableKey?: string;
}

export function StripeCheckoutForm({
  isOpen,
  onClose,
  clientSecret,
  publishableKey,
}: StripeCheckoutFormProps) {
  const presentation = useBookingPresentation();
  const isMobile = useIsMobile();
  const [stripePromise, setStripePromise] = useState<ReturnType<
    typeof loadStripe
  > | null>(null);

  useEffect(() => {
    const key =
      publishableKey || process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;
    if (key) {
      // Swallow load failures (blocked/dropped js.stripe.com — common in
      // in-app browsers) so they don't bubble up as unhandled rejections.
      // The spinner stays until stripePromise resolves; an unloadable Stripe
      // simply leaves it spinning rather than crashing the page.
      setStripePromise(loadStripe(key).catch(() => null));
    }
  }, [publishableKey]);

  if (!clientSecret) {
    return null;
  }

  const title = (
    <Trans
      i18nKey="common:payment.completePayment"
      defaults="Complete Payment"
    />
  );

  // Shared body: the Stripe embedded checkout + a cancel action. Rendered inside
  // a bottom Sheet on mobile and a centered Dialog on desktop.
  const body = (
    <div className="px-6 py-4">
      {stripePromise ? (
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      ) : (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      )}

      <div className="flex justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={onClose}>
          <Trans i18nKey="common:cancel" defaults="Cancel" />
        </Button>
      </div>
    </div>
  );

  // Prevent accidental dismissal mid-payment — closing is via the X or Cancel.
  const preventAutoClose = {
    onInteractOutside: (e: Event) => e.preventDefault(),
    onEscapeKeyDown: (e: KeyboardEvent) => e.preventDefault(),
  };

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          data-native-booking-bottom-sheet
          {...presentation}
          side="bottom"
          className={cn(
            'max-h-[92vh] gap-0 overflow-y-auto rounded-t-2xl p-0',
            presentation.className,
          )}
          {...preventAutoClose}
        >
          <SheetHeader className="border-b px-6 py-4 text-start">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        {...presentation}
        className={cn(
          'max-h-[90vh] max-w-lg overflow-auto p-0 sm:max-w-lg',
          presentation.className,
        )}
        {...preventAutoClose}
      >
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
